import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import { bodyFont, headingFontBold, labelFontSemiBold, vars } from '../../../../../Theme';

import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';

import { mdiAlertCircle, mdiInformation } from '@mdi/js';

import Message from '../../_parts/Message';

import { AdditionalDocStatusEnum } from '../../../../../redux/project/documents/sliceProjectAdditionalDocumentChangeReleaseStatus';
import {
    IChangeAdditionalDocumentStatusErrorState,
    IChangeAdditionalDocumentStatusMainState,
} from '../ChangeAdditionalDocumentStatusDialog';

const selectMenuItemSx: SxProps = {
    fontFamily: labelFontSemiBold,
    '&.Mui-selected': {
        color: vars.brandGreen,
        backgroundColor: 'transparent',
        '&:hover': {
            color: vars.selectedHoveredGreen,
        },
    },
};

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        pl: '14px',
        height: '40px',
    },
};

export default ({
    mainState,
    handleChange,
    showErrorState,
}: IChangeAdditionalDocumentStatusContentProps) => {
    const { t } = useTranslation('translations', {
        keyPrefix: 'changeAdditionalDocumentStatusDialog',
    });

    const { statusOptions } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentChangeReleaseStatus,
    );

    const errorMessage = (
        <FormHelperText sx={{ marginLeft: '0px' }}>
            <Message
                text={t('errorInput')}
                icon={mdiAlertCircle}
                iconSize={0.75}
                color={vars.errorDialogRed}
                typographySx={{
                    fontWeight: 400,
                    fontSize: '12px',
                    ...bodyFont,
                }}
                wrapperSx={{
                    border: 'none',
                    p: 0,
                    m: 0,
                    gap: 0.5,
                }}
            />
        </FormHelperText>
    );

    const isCommentEnabled = useMemo(() => {
        return (
            mainState.command !== null &&
            [AdditionalDocStatusEnum.Activate, AdditionalDocStatusEnum.Deactivate].includes(
                mainState.command,
            )
        );
    }, [mainState.command]);

    return (
        <>
            {statusOptions?.length === 0 ? (
                <Message text={t('noStatusChangePossible')} icon={mdiInformation} />
            ) : (
                <Stack spacing={'12px'}>
                    <FormControl size='small' required>
                        <FormLabel sx={formLabelSx}>{t('selectCommand')}</FormLabel>
                        <Select
                            value={mainState.command || ''}
                            error={showErrorState.command}
                            onChange={(e: SelectChangeEvent<string>) => {
                                handleChange({
                                    field: 'command',
                                    value: e.target.value,
                                });
                            }}
                            displayEmpty
                            renderValue={value =>
                                value ? (
                                    t(value)
                                ) : (
                                    <Typography color={vars.darkGray}>
                                        {t('selectCommandPlaceholder')}
                                    </Typography>
                                )
                            }
                            inputProps={{ 'aria-label': 'Without label' }}
                        >
                            {statusOptions?.map(option => (
                                <MenuItem sx={selectMenuItemSx} key={option} value={option}>
                                    {t(option)}
                                </MenuItem>
                            ))}
                        </Select>
                        {showErrorState.command && errorMessage}
                    </FormControl>

                    {isCommentEnabled && (
                        <FormControl size='small'>
                            <FormLabel sx={formLabelSx}>{t('comment')}</FormLabel>
                            <TextField
                                size='small'
                                sx={textFieldSx}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleChange({ field: 'comment', value: e.target.value })
                                }
                                placeholder={t('commentPlaceholder')}
                                value={mainState.comment}
                                multiline
                            />
                        </FormControl>
                    )}
                </Stack>
            )}
        </>
    );
};

interface IChangeAdditionalDocumentStatusContentProps {
    mainState: IChangeAdditionalDocumentStatusMainState;
    handleChange: ({
        field,
        value,
    }: {
        field: string;
        value: string | AdditionalDocStatusEnum;
    }) => void;
    showErrorState: IChangeAdditionalDocumentStatusErrorState;
}
